import type { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import type { ZodTypeAny } from "zod";
import type { z } from "zod";

import { parseSchema } from "@utils/helpers/zod";

import {
  defaultErrorCode,
  defaultErrorMessage,
  getErrorMessageWithCode,
} from "./error";

export interface ServiceResponse<T> {
  data: T;
}

export class ServiceError extends Error {
  constructor(
    message: string,
    public readonly code: string = "UNKNOWN",
    public readonly status?: number,
  ) {
    super(message);
    this.name = "ServiceError";
  }
}

export const HttpMethod = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  DELETE: "DELETE",
} as const;
export type HttpMethod = (typeof HttpMethod)[keyof typeof HttpMethod];

export interface RequestOptions<T extends ZodTypeAny> {
  params?: Record<string, unknown>;
  data?: unknown;
  responseSchema: T;
  headers?: Record<string, string>;
  timeout?: number;
}

export interface RequestOptionsWithoutSchema {
  params?: Record<string, unknown>;
  data?: unknown;
  headers?: Record<string, string>;
  timeout?: number;
}

interface RequestConfig<T extends ZodTypeAny>
  extends Omit<AxiosRequestConfig, "url" | "method" | "data" | "params"> {
  url: string;
  method: HttpMethod;
  data?: unknown;
  params?: Record<string, unknown>;
  responseSchema: T;
}

interface RequestConfigWithoutSchema
  extends Omit<AxiosRequestConfig, "url" | "method" | "data" | "params"> {
  url: string;
  method: HttpMethod;
  data?: unknown;
  params?: Record<string, unknown>;
}

export abstract class BaseService {
  constructor(protected readonly instance: AxiosInstance) {}

  protected async get<T extends ZodTypeAny>(
    url: string,
    options: RequestOptions<T>,
  ): Promise<ServiceResponse<z.infer<T>>>;
  protected async get<T = unknown>(
    url: string,
    options?: RequestOptionsWithoutSchema,
  ): Promise<ServiceResponse<T>>;
  protected async get<T extends ZodTypeAny>(
    url: string,
    options: RequestOptions<T> | RequestOptionsWithoutSchema = {},
  ): Promise<ServiceResponse<z.infer<T>> | ServiceResponse<unknown>> {
    if ("responseSchema" in options) {
      return this.requestWithSchema({
        url,
        method: HttpMethod.GET,
        ...options,
      });
    }
    return this.requestWithoutSchema({
      url,
      method: HttpMethod.GET,
      ...options,
    });
  }

  protected async post<T extends ZodTypeAny>(
    url: string,
    options: RequestOptions<T>,
  ): Promise<ServiceResponse<z.infer<T>>>;
  protected async post<T = unknown>(
    url: string,
    options?: RequestOptionsWithoutSchema,
  ): Promise<ServiceResponse<T>>;
  protected async post<T extends ZodTypeAny>(
    url: string,
    options: RequestOptions<T> | RequestOptionsWithoutSchema = {},
  ): Promise<ServiceResponse<z.infer<T>> | ServiceResponse<unknown>> {
    if ("responseSchema" in options) {
      return this.requestWithSchema({
        url,
        method: HttpMethod.POST,
        ...options,
      });
    }
    return this.requestWithoutSchema({
      url,
      method: HttpMethod.POST,
      ...options,
    });
  }

  protected async put<T extends ZodTypeAny>(
    url: string,
    options: RequestOptions<T>,
  ): Promise<ServiceResponse<z.infer<T>>>;
  protected async put<T = unknown>(
    url: string,
    options?: RequestOptionsWithoutSchema,
  ): Promise<ServiceResponse<T>>;
  protected async put<T extends ZodTypeAny>(
    url: string,
    options: RequestOptions<T> | RequestOptionsWithoutSchema = {},
  ): Promise<ServiceResponse<z.infer<T>> | ServiceResponse<unknown>> {
    if ("responseSchema" in options) {
      return this.requestWithSchema({
        url,
        method: HttpMethod.PUT,
        ...options,
      });
    }
    return this.requestWithoutSchema({
      url,
      method: HttpMethod.PUT,
      ...options,
    });
  }

  protected async delete<T extends ZodTypeAny>(
    url: string,
    options: RequestOptions<T>,
  ): Promise<ServiceResponse<z.infer<T>>>;
  protected async delete<T = unknown>(
    url: string,
    options?: RequestOptionsWithoutSchema,
  ): Promise<ServiceResponse<T>>;
  protected async delete<T extends ZodTypeAny>(
    url: string,
    options: RequestOptions<T> | RequestOptionsWithoutSchema = {},
  ): Promise<ServiceResponse<z.infer<T>> | ServiceResponse<unknown>> {
    if ("responseSchema" in options) {
      return this.requestWithSchema({
        url,
        method: HttpMethod.DELETE,
        ...options,
      });
    }
    return this.requestWithoutSchema({
      url,
      method: HttpMethod.DELETE,
      ...options,
    });
  }

  private async requestWithSchema<T extends ZodTypeAny>(
    config: RequestConfig<T>,
  ): Promise<ServiceResponse<z.infer<T>>> {
    try {
      const { url, method, data, params, responseSchema, ...axiosConfig } =
        config;

      const response = await this.instance.request({
        url,
        method,
        data,
        params,
        ...axiosConfig,
      });

      const parsedData = parseSchema(response.data, responseSchema);

      return { data: parsedData };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private async requestWithoutSchema<T = unknown>(
    config: RequestConfigWithoutSchema,
  ): Promise<ServiceResponse<T>> {
    try {
      const { url, method, data, params, ...axiosConfig } = config;

      const response = await this.instance.request({
        url,
        method,
        data,
        params,
        ...axiosConfig,
      });

      return { data: response.data as T };
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: unknown): ServiceError {
    if (error instanceof ServiceError) {
      return error;
    }

    if (this.isAxiosError(error)) {
      const status = error.response?.status;
      const { message, code } = getErrorMessageWithCode(
        { body: error.response?.data },
        "This request failed.",
      );

      return new ServiceError(message, code, status);
    }

    const isError = error instanceof Error;
    const message = isError ? error.message : defaultErrorMessage;

    return new ServiceError(message, defaultErrorCode);
  }

  private isAxiosError(error: unknown): error is AxiosError {
    return error !== null && typeof error === "object" && "response" in error;
  }
}
